import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { api } from '../api';
import {
  SMALL_BALANCE_ASSETS,
  SMALL_BALANCE_CONVERT,
  SMALL_BALANCE_CONVERT_TIMEOUT_MS,
  SMALL_BALANCE_HISTORY,
  SMALL_BALANCE_MAX_PAGES,
  SMALL_BALANCE_PAGE_LIMIT
} from '../../constants/smallBalance';
import {
  SmallBalanceAsset,
  SmallBalanceAssets,
  SmallBalanceConvertBody,
  SmallBalanceConvertResult,
  SmallBalanceHistoryPage,
  SmallBalanceHistoryParams
} from './types';

const historyQueryString = (params: SmallBalanceHistoryParams): string => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') query.append(key, String(value));
  });
  return query.toString();
};

export const smallBalanceSlice = api.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Every asset the user can convert right now, across all pages.
     *
     * `page` counts from zero — starting at 1 skips the first page and the
     * list looks empty while `total` is not. `pageLimit` tops out at 50, so
     * walk the pages rather than ask for more.
     */
    getSmallBalanceAssets: build.query<SmallBalanceAssets, void>({
      queryFn: async (_arg, _api, _extraOptions, fetchWithBQ) => {
        const rows: SmallBalanceAsset[] = [];
        let minOutput = '';
        let outputAsset = '';

        for (let page = 0; page < SMALL_BALANCE_MAX_PAGES; page += 1) {
          // eslint-disable-next-line no-await-in-loop
          const response = await fetchWithBQ(
            `${SMALL_BALANCE_ASSETS}?page=${page}&pageLimit=${SMALL_BALANCE_PAGE_LIMIT}`
          );
          if (response.error) {
            return { error: response.error as FetchBaseQueryError };
          }

          const data = (response.data as any)?.data ?? {};
          const pageRows: SmallBalanceAsset[] = Array.isArray(data.rows)
            ? data.rows
            : [];
          rows.push(...pageRows);
          minOutput = data.minOutput ?? minOutput;
          outputAsset = data.outputAsset ?? outputAsset;

          const totalPages = Number(data.totalPages) || 0;
          if (page + 1 >= totalPages || !pageRows.length) break;
        }

        return { data: { rows, minOutput, outputAsset } };
      },
      providesTags: ['SmallBalanceAssets']
    }),

    /**
     * Converts the whole available balance of each asset. One conversion per
     * user at a time: a second request while one runs comes back 409.
     */
    convertSmallBalance: build.mutation<
      SmallBalanceConvertResult,
      SmallBalanceConvertBody
    >({
      query: ({ assets }) => ({
        url: SMALL_BALANCE_CONVERT,
        method: 'POST',
        body: { assets },
        timeout: SMALL_BALANCE_CONVERT_TIMEOUT_MS
      }),
      transformResponse: (response: any) => response?.data,
      // Not the asset list: the small assets page refetches it itself, once
      // the confirm dialog has closed, so the list doesn't change under it.
      invalidatesTags: ['SmallBalanceHistory']
    }),

    getSmallBalanceHistory: build.query<
      SmallBalanceHistoryPage,
      SmallBalanceHistoryParams
    >({
      query: (params) =>
        `${SMALL_BALANCE_HISTORY}?${historyQueryString(params)}`,
      transformResponse: (response: any) => ({
        rows: Array.isArray(response?.data?.rows) ? response.data.rows : [],
        total: Number(response?.data?.total) || 0
      }),
      providesTags: ['SmallBalanceHistory']
    })
  }),
  overrideExisting: false
});

export const {
  useGetSmallBalanceAssetsQuery,
  useConvertSmallBalanceMutation,
  useGetSmallBalanceHistoryQuery
} = smallBalanceSlice;
